import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  useTheme,
  InputAdornment,
} from '@mui/material';
import {
  Save,
  AttachMoney,
  Settings,
  Calculate,
} from '@mui/icons-material';

interface CostSettings {
  basePricePerGram: number;
  setupFee: number;
  minimumOrder: number;
  rushOrderMultiplier: number;
  materialCosts: {
    PLA: number;
    ABS: number;
    PETG: number;
    TPU: number;
  };
  complexityMultipliers: {
    simple: number;
    medium: number;
    complex: number;
  };
  sizeMultipliers: {
    small: number;
    medium: number;
    large: number;
    extraLarge: number;
  };
  features: {
    enableRushOrder: boolean;
    enableBulkDiscount: boolean;
    enableComplexityPricing: boolean;
    enableSizePricing: boolean;
  };
  bulkDiscounts: {
    level1: { threshold: number; discount: number };
    level2: { threshold: number; discount: number };
    level3: { threshold: number; discount: number };
  };
}

interface CostEstimationSettingsProps {
  onNotification: (message: string, severity: 'success' | 'error' | 'info' | 'warning') => void;
}

const CostEstimationSettings: React.FC<CostEstimationSettingsProps> = ({ onNotification }) => {
  const theme = useTheme();
  const [settings, setSettings] = useState<CostSettings>({
    basePricePerGram: 0.15,
    setupFee: 5.00,
    minimumOrder: 10.00,
    rushOrderMultiplier: 1.5,
    materialCosts: {
      PLA: 0.10,
      ABS: 0.12,
      PETG: 0.14,
      TPU: 0.18,
    },
    complexityMultipliers: {
      simple: 1.0,
      medium: 1.3,
      complex: 1.8,
    },
    sizeMultipliers: {
      small: 1.0,
      medium: 1.2,
      large: 1.5,
      extraLarge: 2.0,
    },
    features: {
      enableRushOrder: true,
      enableBulkDiscount: true,
      enableComplexityPricing: true,
      enableSizePricing: true,
    },
    bulkDiscounts: {
      level1: { threshold: 5, discount: 0.05 },
      level2: { threshold: 10, discount: 0.10 },
      level3: { threshold: 20, discount: 0.15 },
    },
  });
  const [loading, setLoading] = useState(false);

  const handleSave = async () => {
    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      onNotification('Cost estimation settings saved successfully!', 'success');
    } catch (error) {
      onNotification('Failed to save settings', 'error');
    } finally {
      setLoading(false);
    }
  };

  const updateSetting = (path: string, value: any) => {
    setSettings(prev => {
      const newSettings = { ...prev };
      const keys = path.split('.');
      let current: any = newSettings;
      
      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      return newSettings;
    });
  };

  const calculateSamplePrice = () => {
    const basePrice = settings.basePricePerGram * 50; // 50g sample
    const materialPrice = settings.materialCosts.PLA * 50;
    const complexityPrice = basePrice * settings.complexityMultipliers.medium;
    const sizePrice = complexityPrice * settings.sizeMultipliers.medium;
    const total = sizePrice + materialPrice + settings.setupFee;
    return Math.max(total, settings.minimumOrder);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Settings sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
        <Typography variant="h5" component="h2" color="primary" sx={{ fontWeight: 600 }}>
          Cost Estimation Settings
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Basic Pricing */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 4 }}>
            <Typography variant="h6" gutterBottom color="primary">
              Basic Pricing
            </Typography>
            <Box sx={{ display: 'grid', gap: 2 }}>
              <TextField
                fullWidth
                label="Base Price per Gram"
                type="number"
                value={settings.basePricePerGram}
                onChange={(e) => updateSetting('basePricePerGram', parseFloat(e.target.value))}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
              <TextField
                fullWidth
                label="Setup Fee"
                type="number"
                value={settings.setupFee}
                onChange={(e) => updateSetting('setupFee', parseFloat(e.target.value))}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
              <TextField
                fullWidth
                label="Minimum Order Value"
                type="number"
                value={settings.minimumOrder}
                onChange={(e) => updateSetting('minimumOrder', parseFloat(e.target.value))}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
              <TextField
                fullWidth
                label="Rush Order Multiplier"
                type="number"
                value={settings.rushOrderMultiplier}
                onChange={(e) => updateSetting('rushOrderMultiplier', parseFloat(e.target.value))}
                InputProps={{
                  endAdornment: <InputAdornment position="end">x</InputAdornment>,
                }}
              />
            </Box>
          </Paper>
        </Grid>

        {/* Material Costs */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 4 }}>
            <Typography variant="h6" gutterBottom color="primary">
              Material Costs (per gram)
            </Typography>
            <Box sx={{ display: 'grid', gap: 2 }}>
              {Object.entries(settings.materialCosts).map(([material, cost]) => (
                <TextField
                  key={material}
                  fullWidth
                  label={`${material} Cost`}
                  type="number"
                  value={cost}
                  onChange={(e) => updateSetting(`materialCosts.${material}`, parseFloat(e.target.value))}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* Complexity Multipliers */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 4 }}>
            <Typography variant="h6" gutterBottom color="primary">
              Complexity Multipliers
            </Typography>
            <Box sx={{ display: 'grid', gap: 2 }}>
              {Object.entries(settings.complexityMultipliers).map(([complexity, multiplier]) => (
                <TextField
                  key={complexity}
                  fullWidth
                  label={`${complexity.charAt(0).toUpperCase() + complexity.slice(1)} Complexity`}
                  type="number"
                  value={multiplier}
                  onChange={(e) => updateSetting(`complexityMultipliers.${complexity}`, parseFloat(e.target.value))}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">x</InputAdornment>,
                  }}
                />
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* Size Multipliers */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 4 }}>
            <Typography variant="h6" gutterBottom color="primary">
              Size Multipliers
            </Typography>
            <Box sx={{ display: 'grid', gap: 2 }}>
              {Object.entries(settings.sizeMultipliers).map(([size, multiplier]) => (
                <TextField
                  key={size}
                  fullWidth
                  label={`${size.charAt(0).toUpperCase() + size.slice(1)} Size`}
                  type="number"
                  value={multiplier}
                  onChange={(e) => updateSetting(`sizeMultipliers.${size}`, parseFloat(e.target.value))}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">x</InputAdornment>,
                  }}
                />
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* Features */}
        <Grid item xs={12}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 4 }}>
            <Typography variant="h6" gutterBottom color="primary">
              Feature Toggles
            </Typography>
            <Grid container spacing={2}>
              {Object.entries(settings.features).map(([feature, enabled]) => (
                <Grid item xs={12} sm={6} md={3} key={feature}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={enabled}
                        onChange={(e) => updateSetting(`features.${feature}`, e.target.checked)}
                      />
                    }
                    label={feature.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                  />
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>

        {/* Bulk Discounts */}
        {settings.features.enableBulkDiscount && (
          <Grid item xs={12}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 4 }}>
              <Typography variant="h6" gutterBottom color="primary">
                Bulk Discounts
              </Typography>
              <Grid container spacing={2}>
                {Object.entries(settings.bulkDiscounts).map(([level, config]) => (
                  <Grid item xs={12} sm={4} key={level}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle2" gutterBottom>
                          {level.charAt(0).toUpperCase() + level.slice(1)} Level
                        </Typography>
                        <TextField
                          fullWidth
                          label="Quantity Threshold"
                          type="number"
                          value={config.threshold}
                          onChange={(e) => updateSetting(`bulkDiscounts.${level}.threshold`, parseInt(e.target.value))}
                          margin="dense"
                        />
                        <TextField
                          fullWidth
                          label="Discount %"
                          type="number"
                          value={config.discount * 100}
                          onChange={(e) => updateSetting(`bulkDiscounts.${level}.discount`, parseFloat(e.target.value) / 100)}
                          margin="dense"
                          InputProps={{
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                          }}
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>
        )}

        {/* Price Preview */}
        <Grid item xs={12}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 4, backgroundColor: theme.palette.grey[50] }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Calculate sx={{ fontSize: 24, color: 'primary.main', mr: 1 }} />
              <Typography variant="h6" color="primary">
                Price Preview
              </Typography>
            </Box>
            <Alert severity="info">
              <Typography variant="body2">
                <strong>Sample Calculation:</strong> 50g PLA print, medium complexity, medium size
                <br />
                <strong>Estimated Price: ${calculateSamplePrice().toFixed(2)}</strong>
              </Typography>
            </Alert>
          </Paper>
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
        <Button
          variant="contained"
          startIcon={<Save />}
          onClick={handleSave}
          disabled={loading}
          size="large"
          sx={{
            background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
            '&:hover': {
              background: `linear-gradient(45deg, ${theme.palette.primary.dark} 30%, ${theme.palette.secondary.dark} 90%)`,
            },
          }}
        >
          {loading ? 'Saving...' : 'Save Settings'}
        </Button>
      </Box>
    </Box>
  );
};

export default CostEstimationSettings;


